import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useLoaderData, useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { DialogType } from '../../common/enums.js';
import {
  devicesMsg,
  durationOfContractMsg,
  elisaDevicesServiceMsg,
  idNumberMsg,
  omaElisaForEmployeeMsg,
  redeemingTheDeviceMsg,
  serviceMsg,
  t,
  terminateAgreementAndReturnDeviceMsg,
  terminationMsg,
} from '../../common/i18n/index.js';
import { scrollTo } from '../../common/utils/browserUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { getEmployeePeriodicPriceAsText } from '../../common/utils/priceUtils';
import { getSubscriptionStatusForEmployee, openSubscriptionDialog } from '../../common/utils/subscriptionUtils.js';
import { SubscriptionAction } from '../../generated/api/subscriptionAction.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useConfig } from '../../public/site/ConfigProvider';
import { openDialog } from '../../public/site/dialogStore.js';
import { BreadCrumbsWithTitle } from '../BreadCrumbsWithTitle/BreadCrumbsWithTitle.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { EppRedeemEmployee } from '../EppRedeem/EppRedeemEmployee.js';
import { Loading } from '../Loading/index.js';
import { getDeviceTypeText } from '../SubscriptionDetails/subscriptionDetailsCommon.js';
import { DeviceSubscriptionExpirationNotification } from './DeviceSubscriptionExpirationNotification.js';
import {
  getCommitmentText,
  getDeviceCommitmentFields,
  getRedeemPrices,
  isDeviceTerminationAllowedForEmployee,
} from './eppSubscriptionUtils.js';
import type { SubscriptionId } from '../../common/constants/pathInterfaces.js';
import type { PersonBillingAccountLoaderData } from '../../common/loaders.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { DialogParams } from '../../common/types/dialog.js';
import type { EmployeeSubscription, SubscriptionStatus } from '../../common/types/subscription.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Contact } from '../../generated/api/contact.js';
import type { BillingAccount } from '../../generated/api/models.js';
import type { Subscription } from '../../generated/api/subscription.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { State } from '../../selfservice/common/store.js';

enum SubscriptionDetailsButtonType {
  EPP_REDEEM,
  EPP_TERMINATE,
  EPP_DAMAGE_OR_SUPPORT_REQUEST,
}

interface DeviceSubscriptionDetailsContentProps {
  subscription: EmployeeSubscription;
  subscriptionStatus: SubscriptionStatus;
  onShowDialog: (params: DialogParams) => void;
  onClickEppMaintenanceRequest: (subscriptionId: string) => void;
  onClickEppRedeem: () => void;
  companyInfo?: CompanyInfoResponse;
}

interface DetailsWrapperContentProps {
  subscriptionId: string;
  subscription?: EmployeeSubscription;
  subscriptionStatus: SubscriptionStatus;
  subscriptionActions?: SubscriptionAction[];
  setShowEppRedeem: (showEppRedeem: boolean) => void;
  onShowDialog: (params: DialogParams) => void;
  companyInfo?: CompanyInfoResponse;
}

interface DetailsWrapperContentEppRedeemProps {
  subscription?: EmployeeSubscription;
  billingAccounts?: BillingAccount[] | null;
  contacts?: Contact[];
  user?: AuthenticatedUser | null;
  setShowEppRedeem: (showEppRedeem: boolean) => void;
}

const getButtons = (
  buttonTypes: SubscriptionDetailsButtonType[],
  onShowDialog: (params: DialogParams) => void,
  subscription: Subscription,
  subscriptionStatus: SubscriptionStatus,
  onClickEppMaintenanceRequest: (subscriptionId: string) => void,
  onClickEppRedeem: () => void,
  companyInfo?: CompanyInfoResponse
): JSX.Element => {
  const buttons = {
    EPP_REDEEM: (
      <CL.Button
        color="link"
        key={SubscriptionDetailsButtonType.EPP_REDEEM}
        onClick={
          subscriptionStatus.hasPendingActions
            ? () => onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING })
            : onClickEppRedeem
        }
      >
        <>
          <span className="ea-icon ea-icon--small ea-icon--invoice" />
          {t.B9EG(redeemingTheDeviceMsg)}
        </>
      </CL.Button>
    ),
    EPP_TERMINATE: (
      <CL.Button
        color="link"
        key={SubscriptionDetailsButtonType.EPP_TERMINATE}
        onClick={() =>
          isDeviceTerminationAllowedForEmployee(subscription)
            ? openSubscriptionDialog(
                onShowDialog,
                {
                  subscription,
                  companyInfo,
                  type: DialogType.EPP_TERMINATE,
                },
                subscriptionStatus.hasPendingActions
              )
            : onShowDialog({
                body: t.ZF77(
                  'The contract cannot be terminated by employee during an active contract period. Please contact your admin user.'
                ),
                header: t.LIUF(terminateAgreementAndReturnDeviceMsg),
                type: DialogType.GENERIC_INFO_DIALOG,
              })
        }
      >
        <>
          <span className="ea-icon ea-icon--small ea-icon--stop" />
          {t.D1IS(terminationMsg)}
        </>
      </CL.Button>
    ),
    EPP_DAMAGE_OR_SUPPORT_REQUEST: (
      <CL.Button
        color="link"
        key={SubscriptionDetailsButtonType.EPP_DAMAGE_OR_SUPPORT_REQUEST}
        onClick={() => onClickEppMaintenanceRequest(subscription.subscriptionId)}
      >
        <>
          <span className="ea-icon ea-icon--small ea-icon--support" />
          {t.N2CC('Device maintenance request')}
        </>
      </CL.Button>
    ),
  };

  // @ts-ignore
  return <>{buttonTypes.map(buttonType => buttons[SubscriptionDetailsButtonType[buttonType].toString()])}</>;
};

const deviceCommitmentFields = (subscription: Subscription): CLDescriptionItem[] => {
  return subscription.details?.device?.eppSubscription
    ? [...getDeviceCommitmentFields(subscription), getRedeemPrices(subscription, true)]
    : [
        {
          title: t.CAA9(durationOfContractMsg),
          description: getCommitmentText(subscription),
        },
      ];
};

const DeviceSubscriptionDetailsContent = ({
  subscription,
  subscriptionStatus,
  onShowDialog,
  onClickEppMaintenanceRequest,
  onClickEppRedeem,
  companyInfo,
}: DeviceSubscriptionDetailsContentProps) => (
  <>
    <CL.Description
      className="of-employee-subscription-details__description"
      items={[
        {
          title: t.YTH3(idNumberMsg),
          description: subscription.subscriptionDisplayId,
        },
        {
          title: 'IMEI',
          description: subscription.details?.device?.serialNumber,
        },
        {
          title: t.CDME(serviceMsg),
          description: subscription.details?.device?.eppSubscription ? t.TRE5(elisaDevicesServiceMsg) : undefined,
        },
        ...deviceCommitmentFields(subscription),
        {
          title: t.RUU7('Your monthly charges'),
          description: getEmployeePeriodicPriceAsText(subscription.details?.monthlyRecurringCharge),
        },
      ]}
    />
    {subscription.details?.device?.eppSubscription && (
      <div className="of-employee-subscription-details__actions">
        {getButtons(
          [
            SubscriptionDetailsButtonType.EPP_REDEEM,
            SubscriptionDetailsButtonType.EPP_DAMAGE_OR_SUPPORT_REQUEST,
            SubscriptionDetailsButtonType.EPP_TERMINATE,
          ],
          onShowDialog,
          subscription,
          subscriptionStatus,
          onClickEppMaintenanceRequest,
          onClickEppRedeem,
          companyInfo
        )}
      </div>
    )}
  </>
);

const DetailsWrapperContentEppRedeem = ({
  subscription,
  billingAccounts,
  contacts,
  user,
  setShowEppRedeem,
}: DetailsWrapperContentEppRedeemProps) => {
  return subscription ? (
    <EppRedeemEmployee
      billingAccounts={billingAccounts}
      contacts={contacts}
      email={user?.email}
      firstName={user?.firstName}
      lastName={user?.lastName}
      loggedInUserContactId={user?.contact?.contactId}
      mobile={user?.mobile}
      onBackClick={() => setShowEppRedeem(false)}
      subscription={subscription}
    />
  ) : (
    <Loading />
  );
};

const DetailsWrapperContent = ({
  subscriptionId,
  subscription,
  subscriptionStatus,
  subscriptionActions,
  setShowEppRedeem,
  onShowDialog,
  companyInfo,
}: DetailsWrapperContentProps) => {
  const navigate = useNavigate();
  return subscription ? (
    <div>
      <DeviceSubscriptionExpirationNotification subscription={subscription} subscriptionActions={subscriptionActions} />
      <DeviceSubscriptionDetailsContent
        subscription={subscription}
        subscriptionStatus={subscriptionStatus}
        onClickEppMaintenanceRequest={() => {
          navigate(generatePath(paths.EMPLOYEE_DEVICE_SUPPORT_REQUEST, { subscriptionId }));
        }}
        onClickEppRedeem={() => setShowEppRedeem(true)}
        onShowDialog={onShowDialog}
        companyInfo={companyInfo}
      />
    </div>
  ) : (
    <Loading />
  );
};

interface EoeSubscriptionDetailsDeviceProps extends SubscriptionId {
  contacts?: Contact[];
  companyInfo?: CompanyInfoResponse;
}

export const EoeSubscriptionDetailsDevice = ({
  subscriptionId,
  companyInfo,
  contacts,
}: EoeSubscriptionDetailsDeviceProps) => {
  const onShowDialog = (params: DialogParams) => openDialog(params);
  const [showEppRedeem, setShowEppRedeem] = useState(false);
  const { billingAccounts: billingAccountsResponse } = useLoaderData() as PersonBillingAccountLoaderData;
  const billingAccounts = billingAccountsResponse.billingAccounts || [];
  const { authenticatedUser } = useAuth();
  const deviceSubs = useSelector((state: State) => state.user?.authenticated?.subscriptions?.device, deepEqual);
  const subscriptionActions = useSelector(
    (state: State) => state.selfservice?.pendingSubscriptionActions?.items || [],
    deepEqual
  );
  const { imagesBaseUrl } = useConfig();
  const subscription = deviceSubs?.find(s => s.subscriptionId === subscriptionId);
  const subscriptionStatus = getSubscriptionStatusForEmployee(subscription, subscriptionActions);

  useEffect(() => {
    if (
      showEppRedeem &&
      subscriptionActions?.some(
        subscriptionAction =>
          subscriptionAction.subscriptionId === subscription?.subscriptionId &&
          [
            SubscriptionAction.StatusEnum.PENDING,
            SubscriptionAction.StatusEnum.PENDING_APPROVAL,
            SubscriptionAction.StatusEnum.ERROR,
          ].includes(subscriptionAction.status)
      )
    ) {
      setShowEppRedeem(false);
      scrollTo(0);
    }
  }, [subscriptionActions, subscription?.subscriptionId, showEppRedeem]);

  const getHeadingBottom = () => {
    if (showEppRedeem) {
      return `${subscription?.subscriptionName} | ${subscription?.subscriptionContactName}`;
    }
    return subscription?.subscriptionSubType ? getDeviceTypeText(subscription.subscriptionSubType) : '';
  };

  return (
    <DetailsWrapper
      classes={['of-employee-subscription-details']}
      detailsTop={
        <>
          {subscription?.subscriptionName && (
            <BreadCrumbsWithTitle
              breadCrumbPaths={[
                { name: t.H6JV(omaElisaForEmployeeMsg), path: paths.EMPLOYEE_HOME },
                { name: t.JJTM(devicesMsg), path: paths.EMPLOYEE_DEVICES },
                { name: subscription.subscriptionName },
              ]}
            />
          )}
        </>
      }
      id="employee-subscription"
      heading={showEppRedeem ? t.B9EG('Redeeming your device') : (subscription?.subscriptionName ?? '')}
      headingBottom={getHeadingBottom()}
      heroImage={!showEppRedeem ? (subscription?.details?.device?.image ?? '') : undefined}
      heroPicto={!showEppRedeem ? 'phone-filled' : ''}
      heroIcon={showEppRedeem ? 'invoice' : ''}
      imagesBaseUrl={imagesBaseUrl}
    >
      {showEppRedeem ? (
        <DetailsWrapperContentEppRedeem
          subscription={subscription}
          billingAccounts={billingAccounts}
          contacts={contacts}
          user={authenticatedUser}
          setShowEppRedeem={setShowEppRedeem}
        />
      ) : (
        <DetailsWrapperContent
          subscriptionId={subscriptionId}
          subscription={subscription}
          subscriptionStatus={subscriptionStatus}
          subscriptionActions={subscriptionActions}
          setShowEppRedeem={setShowEppRedeem}
          onShowDialog={onShowDialog}
          companyInfo={companyInfo}
        />
      )}
    </DetailsWrapper>
  );
};
